import {DateTime} from "luxon";

export const ONE_STAGE_METHOD = 1
export const TWO_STAGE_METHOD = 2
export const THREE_STAGE_METHOD = 3


export class TerminalYear {
  /**
   *
   * @param {Number} finalYearFreeCashFlow
   * @param {Number} longTermGrowthRate
   * @param {Number} forecastedFreeCashFlow
   * @param {Number} divideCapitalizationRate
   * @param {Number} value
   * @param {Number} freeCashFlow
   * @param {Number} dividePresent
   * @param {Number} presentValue
   */
  constructor({
    longTermGrowthRate = 0,
    forecastedFreeCashFlow = 0,
    divideCapitalizationRate = 0,
    freeCashFlow = 0,
  }) {
    this.longTermGrowthRate = longTermGrowthRate
    this.forecastedFreeCashFlow = forecastedFreeCashFlow
    this.divideCapitalizationRate = divideCapitalizationRate
    this.freeCashFlow = freeCashFlow
  }

  get longTermGrowthRateMultiplier() {
    const growthRate = this.longTermGrowthRate / 100

    return 1 + growthRate
  }
}


export default class DiscountedCashFlowMethod {
  /**
   *
   * @param {Number} startYear
   * @param {Number} totalYears
   * @param {Number} discountFactor
   * @param {Number} method
   * @param {Number} growthPercentage
   * @param {Array} EBIT
   * @param {Number} tax
   * @param {Array} reinvestment
   * @param {TerminalYear} terminalYear
   */
  constructor({
    totalYears = 5,
    discountFactor = 0.5,
    method = ONE_STAGE_METHOD,
    growthPercentage = 3.26,
    EBIT = [0,0,0,0,0],
    tax,
    reinvestment = [0,0,0,0,0],
    terminalYear,
    afterTaxCapitalizedEarningsOfSubjectCompany = 0,
    afterTaxNetCashFlowDiscountRate,
    afterTaxFreeCashFlow,
    estimatedDiscretionaryOwnerEarnings,
    analystNote = ["", "", "", ""]
  }) {
    this.totalYears = totalYears
    this.discountFactor = discountFactor
    this.method = method
    this.growthPercentage = growthPercentage
    this.EBIT = EBIT
    this.tax = tax
    this.reinvestment = reinvestment
    this.terminalYear = terminalYear
    this.afterTaxCapitalizedEarningsOfSubjectCompany = afterTaxCapitalizedEarningsOfSubjectCompany
    this.afterTaxNetCashFlowDiscountRate = afterTaxNetCashFlowDiscountRate
    this.afterTaxFreeCashFlow = afterTaxFreeCashFlow
    this.estimatedDiscretionaryOwnerEarnings = estimatedDiscretionaryOwnerEarnings
    this.analystNote = analystNote
  }

  get startYear() {
    return DateTime.now().year
  }

  get yearsArray() {
    const array = [this.startYear]
    for (let i = 1; this.totalYears > i; i+= 1) {
      const year = DateTime.now().plus({ years: i }).year
      array.push(year)
    }

    return array
  }

  get discountFactorArray() {
    return this.yearsArray.map((year, index) => {
      return this.discountFactor + index
    })
  }

  get EBI() {
    return this.EBIT.map((val) => val * (1 - this.tax / 100))
  }

  get freeCashFlow() {
    const multiplicationFactor = 1 + (this.growthPercentage / 100)
    if (this.method !== THREE_STAGE_METHOD) 
      return this.discountFactorArray.map((val, index) =>
        this.afterTaxFreeCashFlow * Math.pow(multiplicationFactor, index)
      )
    else return this.discountFactorArray.map((val, index) =>
      this.EBI[index] - this.reinvestment[index]
    )
  }

  get dividePresent() {
    const baseFactor = 1 + (this.afterTaxNetCashFlowDiscountRate / 100)

    return this.discountFactorArray.map((discountFactor) => {
      return Math.pow(baseFactor, discountFactor)
    })
  }

  get presentValue() {
    return this.freeCashFlow.map((item, index) => item / this.dividePresent[index])
  }

  get finalYearFreeCashFlow() {
    let lastIndex = this.totalYears - 1;

    return this.freeCashFlow[lastIndex];
  }

  get forecastedFreeCashFlow() {
    let multiplyFactor = (this.terminalYear.longTermGrowthRate / 100) + 1;

    return multiplyFactor * this.finalYearFreeCashFlow;
  }

  get terminalYearValue() {
    return this.forecastedFreeCashFlow / this.terminalYear.divideCapitalizationRate;
  }

  get terminalPresentValue() {
    return this.terminalYearValue / this.terminalDividePresent
  }

  get terminalDividePresent() {
    let lastIndex = this.totalYears - 1;

    return this.dividePresent[lastIndex];
  }

  get presentValueSum() {
    return this.presentValue.reduce((partialSum, a) => partialSum + a, 0)
  }

  get valueSubjectCompany() {
    return this.presentValueSum + this.terminalPresentValue;
  }

  get delta() {
    return this.valueSubjectCompany - this.afterTaxCapitalizedEarningsOfSubjectCompany;
  }

  get averageGrowthRate() {
    const { method, growthPercentage, freeCashFlow } = this;

    if (method != THREE_STAGE_METHOD) return growthPercentage;
    else return (Math.pow((freeCashFlow[freeCashFlow.length - 1] / freeCashFlow[0]), 1/(freeCashFlow.length - 1)) - 1) * 100;
  }
}